import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/useAuth.js';
import { requestOtp, verifyOtp } from '../api/admin.js';

type Step = 'mobile' | 'otp';

/** Backend requires E.164; accept bare Indian 10-digit input and add +91. */
function normalizeMobile(raw: string): string {
  const v = raw.replace(/[\s-]/g, '');
  if (/^\d{10}$/.test(v)) return `+91${v}`;
  if (/^91\d{10}$/.test(v)) return `+${v}`;
  return v;
}

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('mobile');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleMobileSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const normalized = normalizeMobile(mobile);
      setMobile(normalized);
      await requestOtp(normalized);
      setStep('otp');
    } catch (err: unknown) {
      const msg = extractError(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleOtpSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await verifyOtp(mobile, otp);
      login(res.accessToken, res.refreshToken, res.user);
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm bg-white rounded-xl shadow-md p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">M8 Admin</h1>
        <p className="text-sm text-gray-500 mb-6">Haron staff portal</p>

        {step === 'mobile' ? (
          <form onSubmit={handleMobileSubmit} className="space-y-4">
            <div>
              <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-1">
                Mobile number
              </label>
              <input
                id="mobile"
                type="tel"
                placeholder="+919700000000"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Sending OTP…' : 'Send OTP'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleOtpSubmit} className="space-y-4">
            <p className="text-sm text-gray-600">
              OTP sent to <span className="font-medium">{mobile}</span>
            </p>
            <div>
              <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
                Enter OTP
              </label>
              <input
                id="otp"
                type="text"
                inputMode="numeric"
                placeholder="6-digit code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                maxLength={6}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Verifying…' : 'Verify & Login'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('mobile'); setOtp(''); setError(''); }}
              className="w-full text-sm text-gray-500 hover:text-gray-700"
            >
              ← Change number
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function extractError(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const r = (err as { response?: { data?: { message?: string; code?: string } } }).response;
    return r?.data?.message ?? r?.data?.code ?? 'Request failed';
  }
  return 'Something went wrong';
}
